import React, { useEffect, useState } from 'react';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Pagination from '../../components/ui/Pagination';
import SearchAndFilter from '../../components/ui/SearchAndFilter';
import { usePaginatedData } from '../../hooks/usePaginatedData';
import { CopyIcon, CheckIcon } from 'lucide-react';
import { toast } from 'sonner';

interface InterviewSession {
  id: string;
  candidate_id: string;
  candidate_name?: string;
  position?: string;
  employment_type?: string;
  organization_id: string;
  status: string;
  current_competency: string;
  questions_asked: number;
  start_time?: string;
  end_time?: string;
  created_at: string;
  noise_consent_given: string;
  noise_level_detected: string;
  switched_tab: string;
  switched_tab_count: number;
  ai_end_reason?: string | null;
  interview_ended_by_ai?: boolean;
  has_report?: boolean;
  report_id?: string | null;
  report_status?: string | null;
}

interface InterviewMessage {
  id: string;
  content: string;
  is_ai: boolean;
  competency?: string;
  created_at: string;
}

interface CreateInterviewForm {
  organizationId: string;
  candidateName: string;
  position: string;
  employmentType: string;
  duration: string;
}

export const Interviews: React.FC = () => {
  const [interviews, setInterviews] = useState<InterviewSession[]>([]);
  const [selectedInterview, setSelectedInterview] = useState<InterviewSession | null>(null);
  const [messages, setMessages] = useState<InterviewMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateUrlModal, setShowCreateUrlModal] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [generatedUrl, setGeneratedUrl] = useState<string>('');
  const [urlCopied, setUrlCopied] = useState(false);
  const [error, setError] = useState<string>('');
  const [reportGenerationSessionId, setReportGenerationSessionId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState<CreateInterviewForm>({
    organizationId: '',
    candidateName: '',
    position: '',
    employmentType: 'Full-time',
    duration: '30'
  });

  // Pagination and filtering
  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData: paginatedInterviews,
    setCurrentPage,
    setItemsPerPage,
    searchQuery,
    setSearchQuery,
    filters,
    setFilter,
    clearFilters,
    totalFilteredItems,
  } = usePaginatedData({
    data: interviews,
    searchFields: ['candidate_id', 'candidate_name', 'position', 'organization_id'],
    itemsPerPageDefault: 10,
  });

  useEffect(() => {
    fetchInterviews();
    fetchOrganizations();
  }, []);

  const handleGenerateReport = async (interview: InterviewSession) => {
    const backendUrl = import.meta.env.VITE_API_BASE_URL;

    try {
      setReportGenerationSessionId(interview.id);
      setError('');

      const response = await fetch(
        `${backendUrl}/api/v1/interview/sessions/${interview.id}/queue-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message =
          (errorData && (errorData.detail || errorData.message)) ||
          `Failed to queue report generation (status ${response.status})`;

        toast.error(message);
        setError(message);
        return;
      }

      toast.success('Report generation has been queued for this session.');

      // Optimistically update local state so the button hides and status is visible
      setInterviews(prev =>
        prev.map(item =>
          item.id === interview.id
            ? { ...item, report_status: 'queued' }
            : item
        )
      );
    } catch (e) {
      console.error('Error queueing report generation:', e);
      toast.error('Failed to queue report generation. Please try again.');
      setError('Failed to queue report generation. Please try again.');
    } finally {
      setReportGenerationSessionId(null);
    }
  };

  const fetchInterviews = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/interviews`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInterviews(data);
      } else {
        setError('Failed to fetch interviews');
      }
    } catch (error) {
      setError('Network error while fetching interviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (sessionId: string) => {
    setMessagesLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/interviews/${sessionId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        setError('Failed to fetch interview messages');
      }
    } catch (error) {
      setError('Network error while fetching messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleViewInterview = async (interview: InterviewSession) => {
    setSelectedInterview(interview);
    setIsModalOpen(true);
    await fetchMessages(interview.id);
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const formatDuration = (startTime?: string, endTime?: string) => {
    if (!startTime) return 'N/A';
    
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = Math.floor((end.getTime() - start.getTime()) / 1000 / 60); // minutes
    
    if (duration < 60) {
      return `${duration}m`;
    } else {
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}h ${minutes}m`;
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const generateCandidateId = () => {
    return 'candidate_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  };

  const handleCreateInterviewUrl = async () => {
    if (!createForm.organizationId || !createForm.candidateName || !createForm.position) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const candidateId = generateCandidateId();
      const baseUrl = window.location.origin;
      
      const params = new URLSearchParams({
        candidateId,
        organizationId: createForm.organizationId,
        candidateName: createForm.candidateName || '',
        candidatePosition: createForm.position,
        employmentType: createForm.employmentType,
        duration: createForm.duration
      });

      const url = `${baseUrl}/welcome?${params.toString()}`;
      setGeneratedUrl(url);
      setUrlCopied(false);
    } catch (error) {
      setError('Failed to generate interview URL');
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 3000);
    } catch (error) {
      setError('Failed to copy URL to clipboard');
    }
  };

  const handleCreateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCreateForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetCreateForm = () => {
    setCreateForm({
      organizationId: '',
      candidateName: '',
      position: '',
      employmentType: 'Full-time',
      duration: '30'
    });
    setGeneratedUrl('');
    setUrlCopied(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Interview Sessions</h1>
          <p className="text-gray-600 mt-2">Monitor and review interview sessions</p>
        </div>
        <Button onClick={() => setShowCreateUrlModal(true)} className="bg-blue-600 hover:bg-blue-700">
          + Create Interview URL
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <SearchAndFilter
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by candidate, position, or organization..."
        filters={[
          {
            label: "Status",
            key: "status",
            value: filters.status || '',
            options: [
              { label: "Pending", value: "pending" },
              { label: "In Progress", value: "in_progress" },
              { label: "Completed", value: "completed" },
              { label: "Cancelled", value: "cancelled" },
            ],
            onChange: (value) => setFilter('status', value),
          },
          {
            label: "Organization",
            key: "organization_id",
            value: filters.organization_id || '',
            options: Array.from(new Set(interviews.map(i => i.organization_id))).map(org => ({
              label: org,
              value: org,
            })),
            onChange: (value) => setFilter('organization_id', value),
          },
        ]}
        onClearFilters={clearFilters}
      />

      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Interviews</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedInterviews.map((interview) => (
                <tr key={interview.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {interview.candidate_name || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">{interview.candidate_id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{interview.position || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{interview.employment_type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{interview.organization_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(interview.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{interview.current_competency}</div>
                    <div className="text-xs text-gray-500">{interview.questions_asked} questions asked</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDuration(interview.start_time, interview.end_time)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {interview.start_time ? new Date(interview.start_time).toLocaleDateString() : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {interview.start_time ? new Date(interview.start_time).toLocaleTimeString() : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleViewInterview(interview)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Details
                      </button>
                      {interview.status === 'completed' &&
                        !interview.has_report &&
                        interview.report_status !== 'queued' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleGenerateReport(interview)}
                            loading={reportGenerationSessionId === interview.id}
                            className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          >
                            Generate Report
                          </Button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {paginatedInterviews.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-2">🎤</div>
              <p className="text-gray-500">
                {totalFilteredItems === 0
                  ? searchQuery || Object.values(filters).some(f => f)
                    ? 'No interviews match your search criteria'
                    : 'No interviews found'
                  : 'No interviews on this page'}
              </p>
              {(searchQuery || Object.values(filters).some(f => f)) && (
                <button
                  onClick={clearFilters}
                  className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear search and filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalFilteredItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      {/* Interview Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`Interview: ${selectedInterview?.candidate_name || selectedInterview?.candidate_id}`}
      >
        {selectedInterview && (
          <div className="space-y-6">
            {/* Interview Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Candidate</h4>
                <p className="text-sm text-gray-900">{selectedInterview.candidate_name || 'Unknown'}</p>
                <p className="text-xs text-gray-500">{selectedInterview.candidate_id}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Position</h4>
                <p className="text-sm text-gray-900">{selectedInterview.position || 'N/A'}</p>
                <p className="text-xs text-gray-500">{selectedInterview.employment_type}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Status</h4>
                {getStatusBadge(selectedInterview.status)}
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Duration</h4>
                <p className="text-sm text-gray-900">
                  {formatDuration(selectedInterview.start_time, selectedInterview.end_time)}
                </p>
              </div>

                <div>
                    <h4 className="text-sm font-medium text-gray-700">Noise Consent</h4>
                    {selectedInterview.noise_consent_given}
                </div>
                <div>
                    <h4 className="text-sm font-medium text-gray-700">Noise Level</h4>
                    {selectedInterview.noise_level_detected}
                </div>

                <div>
                    <h4 className="text-sm font-medium text-gray-700">Switched Tab</h4>
                    {selectedInterview.switched_tab}
                </div>
                <div>
                    <h4 className="text-sm font-medium text-gray-700">Switched Tab Count</h4>
                    {selectedInterview.switched_tab_count}
                </div>
                {selectedInterview.status === 'completed' && selectedInterview.ai_end_reason && (
                    <div className="col-span-2">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">End Reason</h4>
                        <p className="text-sm text-gray-900 bg-blue-50 p-3 rounded-md border-l-4 border-blue-500">
                            {selectedInterview.ai_end_reason}
                        </p>
                    </div>
                )}
            </div>

            {/* Interview Messages */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Interview Conversation</h3>
              
              {messagesLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`p-4 rounded-lg ${
                        message.is_ai
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'bg-green-50 border-l-4 border-green-500'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-sm font-medium ${
                          message.is_ai ? 'text-blue-700' : 'text-green-700'
                        }`}>
                          {message.is_ai ? '🤖 AI Interviewer' : '👤 Candidate'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(message.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{message.content}</p>
                      {message.competency && (
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {message.competency}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {messages.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No messages found for this interview.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Create Interview URL Modal */}
      <Modal
        isOpen={showCreateUrlModal}
        onClose={() => {
          setShowCreateUrlModal(false);
          resetCreateForm();
        }}
        title="Create Interview URL"
      >
        <div className="space-y-4">
          {!generatedUrl ? (
            <>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization *
                  </label>
                  <select
                    name="organizationId"
                    value={createForm.organizationId}
                    onChange={handleCreateFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select an organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name} ({org.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Candidate Name *
                  </label>
                  <input
                    type="text"
                    name="candidateName"
                    value={createForm.candidateName}
                    onChange={handleCreateFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter candidate name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position *
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={createForm.position}
                    onChange={handleCreateFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. Software Engineer"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Type
                  </label>
                  <select
                    name="employmentType"
                    value={createForm.employmentType}
                    onChange={handleCreateFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    name="duration"
                    value={createForm.duration}
                    onChange={handleCreateFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="10"
                    max="120"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowCreateUrlModal(false);
                    resetCreateForm();
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCreateInterviewUrl}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Generate URL
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-green-800 mb-2">Interview URL Generated!</h3>
                <p className="text-sm text-green-700 mb-4">
                  Share this URL with the candidate to start their interview.
                </p>
                
                <div className="bg-white rounded-lg border border-green-300 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 mr-3">
                      <p className="text-sm text-gray-900 break-all">{generatedUrl}</p>
                    </div>
                    <button
                      onClick={copyToClipboard}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        urlCopied
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      {urlCopied ? (
                        <>
                          <CheckIcon className="w-4 h-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <CopyIcon className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowCreateUrlModal(false);
                    resetCreateForm();
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700"
                >
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setGeneratedUrl('');
                    setUrlCopied(false);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Create Another URL
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};